import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import type { VisitExemption } from "@msc/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useDecideExemption, useExemptionInbox } from "../../hooks/useVisitExemptions.js";

/** Zeitpunkt → deutsches Datum in der Zeitzone des Betriebs (nicht UTC). */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Europe/Berlin",
  });
}

/** Freigaben-Block für Vor-Ort-Ausnahmen (#248). */
export function ExemptionInbox() {
  const { t } = useTranslation();
  const { data, isLoading } = useExemptionInbox();

  return (
    <Stack gap="sm">
      <Title order={3}>{t("pages.inbox.exemptionsTitle")}</Title>
      {isLoading ? (
        <Stack gap="sm">
          <Skeleton height={120} radius="sm" />
        </Stack>
      ) : !data || data.length === 0 ? (
        <Text c="dimmed" py="md">
          {t("pages.inbox.exemptionsEmpty")}
        </Text>
      ) : (
        <Stack gap="sm">
          {data.map((item) => (
            <ExemptionCard key={item.id} item={item} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function ExemptionCard({ item }: { item: VisitExemption }) {
  const { t } = useTranslation();
  const approve = useDecideExemption("approve");
  const reject = useDecideExemption("reject");
  const [rejectOpen, { open: openReject, close: closeReject }] = useDisclosure(false);
  const [note, setNote] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [rejectErrorMsg, setRejectErrorMsg] = useState<string | null>(null);

  async function handleApprove() {
    setErrorMsg(null);
    try {
      await approve.mutateAsync({ id: item.id });
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t("pages.inbox.errorApprove"));
    }
  }

  async function handleReject() {
    setRejectErrorMsg(null);
    try {
      // Die Notiz ist optional: leer heißt gar nicht senden (das Backend lehnt "" ab).
      const trimmed = note.trim();
      await reject.mutateAsync(trimmed ? { id: item.id, note: trimmed } : { id: item.id });
      handleCloseReject();
    } catch (err) {
      setRejectErrorMsg(err instanceof Error ? err.message : t("pages.inbox.errorReject"));
    }
  }

  function handleCloseReject() {
    setNote("");
    setRejectErrorMsg(null);
    closeReject();
  }

  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" align="flex-start">
        <Stack gap={4} style={{ flex: 1 }}>
          <Group gap="sm">
            <Text fw={600}>{item.customerName}</Text>
            <Badge variant="light" color="blue" size="sm">
              {item.segmentName}
            </Badge>
            <Badge variant="light" color="yellow" size="sm">
              {t(`pages.visits.exemption.reasons.${item.reason}`)}
            </Badge>
          </Group>
          <Text size="sm">{item.note}</Text>
          <Text size="xs" c="dimmed" mt={4}>
            {t("pages.inbox.exemptionRequestedByAt", {
              name: item.requestedBy.fullName,
              date: formatDate(item.requestedAt),
            })}
          </Text>
        </Stack>
        <Group gap="xs">
          <Button color="green" size="sm" loading={approve.isPending} onClick={handleApprove}>
            {t("pages.inbox.approveButton")}
          </Button>
          <Button color="red" variant="light" size="sm" onClick={openReject}>
            {t("pages.inbox.rejectButton")}
          </Button>
        </Group>
      </Group>

      {errorMsg && (
        <Box mt="xs">
          <Alert color="red">{errorMsg}</Alert>
        </Box>
      )}

      <Modal
        opened={rejectOpen}
        onClose={handleCloseReject}
        title={t("pages.inbox.exemptionRejectTitle")}
      >
        <Stack gap="sm">
          {rejectErrorMsg && (
            <Alert color="red">{rejectErrorMsg}</Alert>
          )}
          <Textarea
            label={t("pages.inbox.exemptionRejectNote")}
            placeholder={t("pages.inbox.rejectReasonPlaceholder")}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            autosize
            minRows={3}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={handleCloseReject}>
              {t("common.cancel")}
            </Button>
            <Button color="red" onClick={handleReject} loading={reject.isPending}>
              {t("pages.inbox.rejectButton")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
